import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SHARED_ROUTES } from '../routes';
import type { Mode, ConsultationState } from '../types/graph';
import { useResetQuestionnaire } from './useResetQuestionnaire';
import type { ResetOptions } from './useResetQuestionnaire';

export interface UseEntryNavigationReturn {
  navigateToLanding: (mode: Mode, consultation: ConsultationState) => string;
  navigateToPatientSelect: () => string;
  getNextRouteForProfessional: () => string;
  handleReset: (options?: ResetOptions) => Promise<void>;
  goTo: (path: string) => void;
}

export function useEntryNavigation(): UseEntryNavigationReturn {
  const navigate = useNavigate();
  const resetQuestionnaire = useResetQuestionnaire();

  const navigateToLanding = useCallback(
    (mode: Mode, consultation: ConsultationState): string => {
      if (mode?.includes('LINK') && consultation.professional?.slug) {
        return `/professional/${consultation.professional.slug}`;
      }

      if (mode === 'INVITATION' && consultation.practice) {
        if (consultation.noAppointment) {
          return SHARED_ROUTES.ERROR;
        }
        return SHARED_ROUTES.LANDING_PAGE;
      }

      return SHARED_ROUTES.ERROR;
    },
    [],
  );

  const navigateToPatientSelect = useCallback((): string => {
    return SHARED_ROUTES.SELF_IDENTIFICATION_QUESTION;
  }, []);

  const getNextRouteForProfessional = useCallback(
    (): string =>
      // Always route to the staging landing page and let NavigationProvider
      // compute flow[0]. Assisted (pro-launched) entries are handled uniformly
      // by the orchestration transformer via the ASSISTED_BY_PRO flag.
      SHARED_ROUTES.LANDING_PAGE,
    [],
  );

  const handleReset = useCallback(
    async (options?: ResetOptions): Promise<void> => {
      await resetQuestionnaire(options);
    },
    [resetQuestionnaire],
  );

  const goTo = useCallback(
    (path: string): void => {
      navigate(path);
    },
    [navigate],
  );

  return {
    navigateToLanding,
    navigateToPatientSelect,
    getNextRouteForProfessional,
    handleReset,
    goTo,
  };
}
